import React from 'react';
import { Warning } from '@phosphor-icons/react';

import { Icon } from '../../../design-system/components/Icon';
import { Button, Pagination, Select, Table, Toolbar } from '../../../design-system/components/desktop';
import { ConsoleEmpty, ConsoleError, StatusPill } from '../../../design-system/patterns/console';
import { OrderItemsCell, shortOrderId } from '../../Dashboard/dashboard.parts';
import { formatCurrency } from '../../../utils/common';

import '../../Dashboard/dashboard.css';

/**
 * Every order on the platform, the view. Pure.
 *
 * This file lives in `SellerDashboard/OrderTable/` but is mounted by
 * `/admin-dashboard`. It is converted here because it is in this directory and
 * because it was the sixth importer of the `StatusBadge` component this batch
 * deletes. Its default export, path and props are unchanged, so the admin
 * dashboard did not need touching.
 *
 * THE NEEDS-ATTENTION BANNER IS A FILTER, SO IT IS A BUTTON THAT LOOKS LIKE
 * ONE. It used to be a `<button>` wearing seven inline styles built out of
 * `rgba(245, 158, 11, 0.15)` and `#f59e0b`, a hardcoded amber that ignored the
 * theme entirely. It is a ghost `Button` with a warning glyph now, and it still
 * does exactly what it did: sets the status filter to `attention` and resets to
 * page 1.
 */
export function AllOrderView({
  rows = [],
  loading = false,
  error,
  onRetry,

  search = '',
  onSearchChange,
  status = '',
  onStatusChange,
  statusOptions = [],

  needsAttentionCount = 0,
  onShowAttention,

  page = 1,
  pageCount = 1,
  pageSize = 10,
  total = 0,
  onPageChange,

  onView,
  drawer = null,
}) {
  const attentionMode = status === 'attention';
  const filtered = Boolean(search || status);

  const columns = [
    { key: 'order_id', header: 'Order', render: (row) => shortOrderId(row.order_id) },
    {
      key: 'seller',
      header: 'Seller',
      render: (row) => row.seller || row.seller_name || 'Not recorded',
    },
    {
      key: 'items',
      header: 'Items',
      render: (row) => <OrderItemsCell items={row.items} />,
    },
    {
      key: 'total',
      header: 'Total',
      numeric: true,
      render: (row) => formatCurrency(row.orderTotal),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <StatusPill status={row.derivedStatus} />,
    },
  ];

  return (
    <>
      <Table
        columns={columns}
        data={error ? [] : rows}
        rowKey={(row, index) => row.order_id || index}
        loading={loading}
        onRowClick={(row) => onView && onView(row)}
        emptyState={
          error ? (
            <ConsoleError error={error} onRetry={onRetry} title="Could not load orders" />
          ) : (
            <ConsoleEmpty
              filtered={filtered}
              title="No orders yet"
              text="Every order placed on the marketplace appears here."
              filteredText="No order matches that search or status. Clear one of them and the orders come back."
              onClear={() => {
                if (onSearchChange) onSearchChange('');
                if (onStatusChange) onStatusChange('');
              }}
            />
          )
        }
        rowActions={(row) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={(event) => {
              event.stopPropagation();
              onView(row);
            }}
          >
            View order
          </Button>
        )}
        toolbar={
          <Toolbar
            search={search}
            onSearchChange={onSearchChange}
            searchPlaceholder="Filter by order, seller or card"
            searchLabel="Filter all orders"
            count={loading ? undefined : `${total} ${total === 1 ? 'order' : 'orders'}`}
            filters={
              <div className="dsa-dash__filters">
                <Select
                  options={statusOptions}
                  value={status}
                  onChange={onStatusChange}
                  placeholder="All statuses"
                  label="Filter by order status"
                />
                {!attentionMode && needsAttentionCount > 0 ? (
                  <Button variant="ghost" size="sm" onClick={onShowAttention}>
                    <Icon as={Warning} size="sm" active />
                    {needsAttentionCount} need{needsAttentionCount === 1 ? 's' : ''} attention
                  </Button>
                ) : null}
              </div>
            }
          />
        }
        pagination={
          pageCount > 1 ? (
            <Pagination
              page={page}
              pageCount={pageCount}
              pageSize={pageSize}
              totalItems={total}
              itemNoun="orders"
              onPageChange={onPageChange}
            />
          ) : null
        }
      />
      {drawer}
    </>
  );
}

export default AllOrderView;
